import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import {
  Button,
  Table,
  TableBody,
  TableBodyData,
  TableBodyDataCellTemplate,
  TableBodyDataRowTemplate,
  TableFilterTextInput,
  TableFilterNone,
  TableFilters,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TablePagination,
  TableSortingRule,
  TableSortingRules,
  TableWrapper,
} from "@utahdts/utah-design-system";
import {
  GET_ALL_SITE_VISIT_OPTIONS,
} from "../../queries/siteVisitQrys/siteVisitOptionsQrys";
import OptionEntryModal from "./OptionEntryModal";
import "./siteVisits.css";

const ManageOptionEntries = () => {
  const user = localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user"))
    : { isPublic: true };

  const canEdit = user?.permissions?.includes("edit_survey_question");

  const { data, loading, error, refetch } = useQuery(GET_ALL_SITE_VISIT_OPTIONS);
  const options = data?.getSiteVisitOptions ?? [];
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const [modalOpen, setModalOpen] = useState(false);
  const [isNewOption, setIsNewOption] = useState(false);
  const [selectedOptionPk, setSelectedOptionPk] = useState(null);

  const handleOpenNew = () => {
    setIsNewOption(true);
    setSelectedOptionPk(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (option) => {
    setIsNewOption(false);
    setSelectedOptionPk(option.id);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedOptionPk(null);
  };

  if (error) {
    return (
      <div className="utah-design-system page-container">
        <h3>Manage Options</h3>
        <p>You do not have access to view this page.</p>
      </div>
    );
  }

  return (
    <div className="utah-design-system page-container">
      <h3>Manage Options</h3>

      {canEdit && (
        <Button
          appearance="solid"
          variant="primary"
          className="mb-spacing mt-spacing"
          onClick={handleOpenNew}
        >
          New Option
        </Button>
      )}

      {loading && <p>Loading...</p>}

      {!loading && options.length > 0 && (
        <TableWrapper>
          <div
            className={`site-visits-show-entries ${!canEdit ? "site-visits-show-entries--no-edit" : ""}`.trim()}
          >
            <span>Show</span>
            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              className="site-visits-show-entries__select"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span>entries</span>
          </div>
          <Table className="table--alt">
            <TableHead>
              <TableSortingRules>
                <TableSortingRule allyLabel="Sort by Id" recordFieldPath="id" />
                <TableSortingRule allyLabel="Sort by Display Name" recordFieldPath="displayName" />
                <TableSortingRule allyLabel="Sort by Status" recordFieldPath="status" />
              </TableSortingRules>
              <TableHeadRow>
                <TableHeadCell recordFieldPath="id">Id</TableHeadCell>
                <TableHeadCell recordFieldPath="displayName">Display Name</TableHeadCell>
                <TableHeadCell recordFieldPath="status">Status</TableHeadCell>
                <TableHeadCell recordFieldPath="actions"></TableHeadCell>
              </TableHeadRow>
              <TableFilters>
                <TableFilterTextInput
                  a11yLabel="Filter by Id"
                  recordFieldPath="id"
                  placeholder="filter"
                />
                <TableFilterTextInput
                  a11yLabel="Filter by Display Name"
                  recordFieldPath="displayName"
                  placeholder="filter"
                />
                <TableFilterTextInput
                  a11yLabel="Filter by Status"
                  recordFieldPath="status"
                  placeholder="filter"
                />
                <TableFilterNone aria-label="Actions">
                  <span className="visually-hidden">Actions</span>
                </TableFilterNone>
              </TableFilters>
            </TableHead>
            <TableBody>
              <TableBodyData records={options} recordIdField="id">
                <TableBodyDataRowTemplate>
                  <TableBodyDataCellTemplate recordFieldPath="id" />
                  <TableBodyDataCellTemplate recordFieldPath="displayName" />
                  <TableBodyDataCellTemplate recordFieldPath="status" />
                  <TableBodyDataCellTemplate>
                    {({ record }) => (
                      <button
                        className="link-button"
                        onClick={() => handleOpenEdit(record)}
                      >
                        {canEdit ? "Edit" : "View"}
                      </button>
                    )}
                  </TableBodyDataCellTemplate>
                </TableBodyDataRowTemplate>
              </TableBodyData>
            </TableBody>
          </Table>
          {options.length > itemsPerPage && (
            <TablePagination
              className="mt-spacing"
              id="manage-options-pagination"
              itemsPerPage={itemsPerPage}
            />
          )}
        </TableWrapper>
      )}

      {modalOpen && (
        <OptionEntryModal
          onClose={handleCloseModal}
          isNewOption={isNewOption}
          selectedOptionPk={selectedOptionPk}
          canEdit={canEdit}
          refetch={refetch}
        />
      )}
    </div>
  );
};

export default ManageOptionEntries;
